import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Eye, EyeOff } from 'lucide-react';

const AdminLogin = ({ onLogin, language }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        localStorage.setItem('admin_token', result.token);
        onLogin(result.token);
      } else {
        setError(result.message || 'Identifiants incorrects');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1c47] flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center text-white relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, #9dbc1d 0%, #52a385 15%, #f3a114 30%, #e62c86 60%, #c738a7 80%, #74388e 100%)'
        }}>
          <CardTitle className="flex items-center justify-center gap-3 text-xl relative z-10">
            <Lock className="w-6 h-6" />
            {language === 'fr' ? 'Administration WEDO' : 'WEDO Administration'}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="flex items-center gap-2 text-gray-700 font-medium">
                {language === 'fr' ? 'Nom d\'utilisateur' : 'Username'}
              </Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                placeholder={language === 'fr' ? 'Entrez votre nom d\'utilisateur' : 'Enter your username'}
                required
                className="w-full border-gray-300 focus:border-[#e62c86] focus:ring-[#e62c86]/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2 text-gray-700 font-medium">
                {language === 'fr' ? 'Mot de passe' : 'Password'}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder={language === 'fr' ? 'Entrez votre mot de passe' : 'Enter your password'}
                  required
                  className="w-full border-gray-300 focus:border-[#e62c86] focus:ring-[#e62c86]/20 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <Button 
              type="submit"
              disabled={loading || !credentials.username || !credentials.password}
              className="w-full text-white mt-6 py-3 font-semibold shadow-lg transition-all duration-200 disabled:opacity-50 border-0"
              style={{
                background: (!loading && credentials.username && credentials.password) ? 
                  'linear-gradient(135deg, #9dbc1d 0%, #52a385 15%, #f3a114 30%, #e62c86 60%, #c738a7 80%, #74388e 100%)' : 
                  'rgba(107, 114, 128, 0.5)'
              }}
            >
              {loading ? (
                language === 'fr' ? 'Connexion...' : 'Connecting...'
              ) : (
                language === 'fr' ? 'Se connecter' : 'Login'
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              {language === 'fr' ? 
                'Accès réservé aux administrateurs WEDO' :
                'Access reserved for WEDO administrators'
              }
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;